/*
 * Where /auth/callback goes next. The intent travels in the OAuth `state` (MSAL returns it from
 * handleRedirectPromise at boot, after checking it matches the request it sent) and is kept in memory
 * for the page load that follows the redirect. It names only a path on this site or the origin of the
 * app a new account came from — never anything entered on the pre-screen.
 */

export type PostAuthIntent =
  /** A sign-in that started on a page of this site: go back there. */
  | { kind: 'path'; path: string }
  /** A new account (`prompt=create`); `returnOrigin` is re-checked against the allow-list on arrival. */
  | { kind: 'created'; returnOrigin: string | null };

const PATH_PREFIX = 'path:';
const CREATED_PREFIX = 'created:';

/** A path on this site (not `//host` or `/\host`, which browsers treat as another origin). */
export function isSitePath(value: string): boolean {
  return value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\');
}

export function encodeState(intent: PostAuthIntent): string {
  return intent.kind === 'path'
    ? `${PATH_PREFIX}${isSitePath(intent.path) ? intent.path : '/'}`
    : `${CREATED_PREFIX}${intent.returnOrigin ?? ''}`;
}

export function decodeState(state: string | null | undefined): PostAuthIntent | null {
  if (typeof state !== 'string') return null;
  if (state.startsWith(PATH_PREFIX)) {
    const path = state.slice(PATH_PREFIX.length);
    return isSitePath(path) ? { kind: 'path', path } : null;
  }
  if (state.startsWith(CREATED_PREFIX)) {
    const origin = state.slice(CREATED_PREFIX.length);
    return { kind: 'created', returnOrigin: origin === '' ? null : origin };
  }
  return null;
}

let current: PostAuthIntent | null = null;

/** Set once at boot from the redirect response (or the mock session); null clears it. */
export function setPostAuthIntent(intent: PostAuthIntent | null): void {
  current = intent;
}

/** The intent of this page load's sign-in redirect, if there was one. */
export function postAuthIntent(): PostAuthIntent | null {
  return current;
}
